import {QuestionBody, QuestionTitle} from '../../../components/Layout/question';
import React, {useEffect} from 'react';
import {
  FormGroup,
  FormItemGroup,
  RequireMarker,
  TimesInput,
} from '../../../../../components/form-items';
import Select from '../../../../../components/Select/Select';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMinus, faPlusCircle} from '@fortawesome/free-solid-svg-icons';
import {IconButton} from '../../../../../components/Button';
import {useFormContext, Controller, useFieldArray} from 'react-hook-form';
import {isNilOrEmpty} from '../../../utils/validate';
import {timeToDateTime} from '../../../utils/time';
import {AttendanceQuestions} from '../../../api/types';
import {Interval} from 'luxon';
import BaselineInfo from '../../../components/info-tip/baseline-info';
import BreakTimeInfo from '../../../components/info-tip/break-time-info';
import StartDayTimeInfo from '../../../components/info-tip/start-day-time-info';

type ShiftScheduleQuestionProps = {
  onChange: () => void;
};
function NormalShiftQuestion({
  onChange: onDataChange,
}: ShiftScheduleQuestionProps) {
  const {
    control,
    formState: {errors, isSubmitting, isSubmitted},
    getValues,
    trigger,
  } = useFormContext();
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'standardWorkingTime.restTimes',
  });

  useEffect(() => {
    if (fields.length === 0) {
      append({
        startTime: '12:00',
        endTime: '13:00',
      });
    }
  }, [fields]);
  return (
    <QuestionBody>
      <QuestionTitle>Q1：請問貴公司的「標準」上下班時間 ?</QuestionTitle>
      <div>
        <FormGroup columnTemplates={2} data-test-id={'base-line'}>
          <FormItemGroup>
            <label htmlFor={'baseLine'}>
              班別工時基準
              <BaselineInfo />
              <RequireMarker />
            </label>
            <Controller
              name={'standardWorkingTime.baseLine'}
              id={'baseline'}
              control={control}
              rules={{
                required: '班別工時基準為必填',
                min: {
                  value: 1,
                  message: '班別工時基準不可小於 1',
                },
              }}
              render={({onChange, value}) => (
                <TimesInput
                  error={errors.standardWorkingTime?.baseLine?.message}
                  onChange={v => {
                    onChange(v);
                    onDataChange();
                  }}
                  disabled={isSubmitting}
                  value={value}
                />
              )}
            />
          </FormItemGroup>
          <FormItemGroup data-test-id={'day-start-time'}>
            <label htmlFor={'dayStartTime'}>
              日切換時間
              <StartDayTimeInfo />
              <RequireMarker />
            </label>
            <Controller
              name={'standardWorkingTime.dayStartTime'}
              render={({onChange, value}) => (
                <Select
                  id={'dayStartTime'}
                  placeholder={'請選擇日切換時間'}
                  error={errors.standardWorkingTime?.dayStartTime?.message}
                  options={new Array(24).fill(1).map((v, idx) => ({
                    value: `${idx.toString().padStart(2, '0')}:00`,
                    label: `${idx.toString().padStart(2, '0')}:00`,
                  }))}
                  value={{
                    value: value,
                    label: value,
                  }}
                  onChange={(o: {value: string; label: string}) => {
                    onChange(o.value);
                    onDataChange();
                  }}
                  disabled={isSubmitting}
                />
              )}
              rules={{
                required: '日切換時間為必填欄位',
                validate: dayStartTime => {
                  const {
                    standardWorkingTime: {workOnTime, workOffTime},
                  } = getValues();
                  if (
                    isNilOrEmpty(workOffTime) ||
                    isNilOrEmpty(workOnTime) ||
                    isNilOrEmpty(dayStartTime)
                  ) {
                    return true;
                  }
                  const start = timeToDateTime(workOnTime);
                  const end = timeToDateTime(workOffTime, start);
                  const baseLine = timeToDateTime(dayStartTime, start);

                  return Interval.fromDateTimes(start, end).contains(baseLine)
                    ? '上班時間區間不可超過日切換時間'
                    : true;
                },
              }}
            />
          </FormItemGroup>
        </FormGroup>
        <FormGroup columnTemplates={2} data-test-id={'work-on-time'}>
          <FormItemGroup>
            <label>
              上班時間 <RequireMarker />
            </label>
            <Controller
              name={'standardWorkingTime.workOnTime'}
              control={control}
              render={({onChange, value}) => (
                <TimesInput
                  onChange={v => {
                    onChange(v);
                    onDataChange();
                  }}
                  error={errors.standardWorkingTime?.workOnTime?.message}
                  value={value}
                  disabled={isSubmitting}
                />
              )}
              rules={{
                required: '上班時間為必填',
              }}
            />
          </FormItemGroup>
          <FormItemGroup data-test-id={'work-off-time'}>
            <label>
              下班時間 <RequireMarker />
            </label>
            <Controller
              name={'standardWorkingTime.workOffTime'}
              control={control}
              render={({onChange, value}) => (
                <TimesInput
                  error={errors.standardWorkingTime?.workOffTime?.message}
                  onChange={v => {
                    onChange(v);
                    onDataChange();
                  }}
                  value={value}
                  disabled={isSubmitting}
                />
              )}
              rules={{
                required: '下班班時間為必填',
              }}
            />
          </FormItemGroup>
        </FormGroup>
        <h2 className={'col-start-1 col-end-4 text-[22px] font-bold mb-2'}>
          休息時間
          <BreakTimeInfo />
        </h2>
        {fields?.map((t, idx) => {
          return (
            <FormGroup
              columnTemplates={'1fr auto 1fr auto'}
              key={t.id}
              data-test-id={'rest-time'}
            >
              <FormItemGroup>
                <label>
                  休息開始時間
                  <RequireMarker />
                </label>
                <Controller
                  name={`standardWorkingTime.restTimes.${idx}.startTime`}
                  render={({onChange, value}) => (
                    <div data-test-id={`rest-start-time-${idx}`}>
                      <TimesInput
                        error={
                          errors.standardWorkingTime?.restTimes &&
                          errors.standardWorkingTime?.restTimes[idx]?.startTime
                            ?.message
                        }
                        onChange={async v => {
                          onChange(v);
                          if (isSubmitted) {
                            await trigger(
                              `standardWorkingTime.restTimes.${idx}.endTime`,
                            );
                          }
                          onDataChange();
                        }}
                        value={value}
                        disabled={isSubmitting}
                      />
                    </div>
                  )}
                  rules={{
                    required: '休息開始時間為必填',
                    validate: restStart => {
                      const values = getValues() as AttendanceQuestions;
                      const restEnd = getValues(
                        `standardWorkingTime.restTimes.${idx}.endTime`,
                      );
                      const {
                        standardWorkingTime: {
                          restTimes,
                          workOnTime,
                          workOffTime,
                        },
                      } = values;

                      if (isNilOrEmpty(restStart)) {
                        return '休息開始時間為必填';
                      }

                      const workStart = isNilOrEmpty(workOnTime)
                        ? undefined
                        : timeToDateTime(workOnTime);
                      const workEnd = isNilOrEmpty(workOffTime)
                        ? undefined
                        : timeToDateTime(workOffTime, workStart);
                      const restStartDate = isNilOrEmpty(restStart)
                        ? undefined
                        : timeToDateTime(restStart, workStart);
                      const restEndDate = isNilOrEmpty(restEnd)
                        ? undefined
                        : timeToDateTime(restEnd, workStart);

                      if (workStart && workEnd) {
                        const startDateTime = timeToDateTime(
                          restStart,
                          workStart,
                        );
                        if (
                          !Interval.fromDateTimes(workStart, workEnd).contains(
                            startDateTime,
                          )
                        ) {
                          return '休息開始時間需介於上班時間區間內';
                        }
                        if (restStartDate && restEndDate) {
                          if (
                            Interval.fromDateTimes(
                              workStart,
                              restStartDate,
                            ).contains(restEndDate)
                          ) {
                            return '休息結束時間不能早於休息開始時間';
                          }
                        }
                      }

                      if (isNilOrEmpty(restTimes[idx].endTime)) {
                        return true;
                      }

                      const currentCompare = timeToDateTime(restStart);

                      const overlapTimes = restTimes
                        .filter(
                          (t, tIdx) =>
                            !isNilOrEmpty(t.startTime) &&
                            !isNilOrEmpty(t.endTime) &&
                            tIdx !== idx,
                        )
                        .map(t => {
                          const start = timeToDateTime(t.startTime);
                          const end = timeToDateTime(t.endTime, start);
                          return Interval.fromDateTimes(start, end);
                        })
                        .filter(
                          i =>
                            i.contains(currentCompare) &&
                            i.end.toMillis() !== currentCompare.toMillis(),
                        );

                      return overlapTimes.length > 0
                        ? '休息時間不可重疊'
                        : true;
                    },
                  }}
                />
              </FormItemGroup>
              <FontAwesomeIcon icon={faMinus} className={'mt-[35px]'} />
              <FormItemGroup>
                <label>
                  休息結束時間
                  <RequireMarker />
                </label>
                <Controller
                  name={`standardWorkingTime.restTimes.${idx}.endTime`}
                  render={({onChange, value}) => (
                    <div data-test-id={`rest-end-time-${idx}`}>
                      <TimesInput
                        error={
                          errors.standardWorkingTime?.restTimes &&
                          errors.standardWorkingTime?.restTimes[idx]?.endTime
                            ?.message
                        }
                        onChange={async v => {
                          onChange(v);
                          if (isSubmitted) {
                            await trigger(
                              `standardWorkingTime.restTimes.${idx}.startTime`,
                            );
                          }
                          onDataChange();
                        }}
                        value={value}
                        disabled={isSubmitting}
                      />
                    </div>
                  )}
                  rules={{
                    required: '休息結束時間為必填',
                    validate: restEnd => {
                      const values = getValues() as AttendanceQuestions;
                      const restStart = getValues(
                        `standardWorkingTime.restTimes.${idx}.startTime`,
                      );
                      const {
                        standardWorkingTime: {
                          restTimes,
                          workOnTime,
                          workOffTime,
                        },
                      } = values;

                      if (isNilOrEmpty(restEnd)) {
                        return '休息結束時間為必填';
                      }

                      const workStart = isNilOrEmpty(workOnTime)
                        ? undefined
                        : timeToDateTime(workOnTime);
                      const workEnd = isNilOrEmpty(workOffTime)
                        ? undefined
                        : timeToDateTime(workOffTime, workStart);
                      const restStartDate = isNilOrEmpty(restStart)
                        ? undefined
                        : timeToDateTime(restStart, workStart);
                      const restEndDate = isNilOrEmpty(restEnd)
                        ? undefined
                        : timeToDateTime(restEnd, workStart);

                      if (workStart && workEnd) {
                        const endDateTime = timeToDateTime(restEnd, workStart);
                        if (
                          !Interval.fromDateTimes(workStart, workEnd).contains(
                            endDateTime,
                          )
                        ) {
                          return '休息結束時間需介於上班時間區間內';
                        }
                        if (restStartDate && restEndDate) {
                          if (
                            Interval.fromDateTimes(
                              workStart,
                              restStartDate,
                            ).contains(restEndDate)
                          ) {
                            return '休息結束時間不能早於休息開始時間';
                          }
                        }
                      }

                      if (isNilOrEmpty(restTimes[idx].startTime)) {
                        return true;
                      }

                      const currentCompare = timeToDateTime(restEnd);

                      const overlapTimes = restTimes
                        .filter(
                          (t, tIdx) =>
                            !isNilOrEmpty(t.startTime) &&
                            !isNilOrEmpty(t.endTime) &&
                            tIdx !== idx,
                        )
                        .map(t => {
                          const start = timeToDateTime(t.startTime);
                          const end = timeToDateTime(t.endTime, start);
                          return Interval.fromDateTimes(start, end);
                        })
                        .filter(
                          i =>
                            i.contains(currentCompare) &&
                            i.start.toMillis() !== currentCompare.toMillis(),
                        );

                      return overlapTimes.length > 0
                        ? '休息時間不可重疊'
                        : true;
                    },
                  }}
                />
              </FormItemGroup>
              <div className="mt-[32px]">
                {fields.length > 1 && (
                  <IconButton
                    onClick={() => {
                      remove(idx);
                      onDataChange();
                    }}
                    disabled={isSubmitting}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'trash-alt']}
                      className={'text-lg'}
                    />
                  </IconButton>
                )}
              </div>
            </FormGroup>
          );
        })}
        {fields.length < 3 && (
          <IconButton
            onClick={() => {
              append({
                startTime: '',
                endTime: '',
              });
              onDataChange();
            }}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faPlusCircle} style={{fontSize: 28}} />
            新增休息時間
          </IconButton>
        )}
      </div>
    </QuestionBody>
  );
}

export default NormalShiftQuestion;
